import {
    api, C, sampleFields, sampleInventory, sampleTreatments,
    FRUITETI_CROPS, OPEN_FIELD_CROP_GROUPS, WMO_CODES,
    PERMANENT_CROP_KEYWORDS, OFFLINE_QUEUE_KEY
} from './context.js';
import {
    parseDateRobust, formatActivityTypeLabel, activityMatchesField,
    calculatePolygonArea, checkParticelleContiguita
} from './utils.js';
import { showRegisterRequiredAlert } from './auth.js';
import { switchTab } from './nav.js';
import { loadApplicationData } from './data.js';
import { renderFullLogTable } from './activities.js';
export function populateCropSelect(selectEl, type, { placeholder = null } = {}) {
    if (!selectEl) return;
    selectEl.innerHTML = '';
    if (placeholder) {
        const empty = document.createElement('option');
        empty.value = '';
        empty.textContent = placeholder;
        selectEl.appendChild(empty);
    }
    if (type === 'frutteti') {
        FRUITETI_CROPS.forEach(crop => {
            const opt = document.createElement('option');
            opt.value = crop;
            opt.textContent = crop === 'Altro' ? 'Altro (specificare)' : crop;
            selectEl.appendChild(opt);
        });
        return;
    }
    OPEN_FIELD_CROP_GROUPS.forEach(group => {
        const optgroup = document.createElement('optgroup');
        optgroup.label = group.label;
        group.crops.forEach(crop => {
            const opt = document.createElement('option');
            opt.value = crop;
            opt.textContent = crop === 'Altro' ? 'Altro (specificare)' : crop;
            optgroup.appendChild(opt);
        });
        selectEl.appendChild(optgroup);
    });
}

export function initCropSelectOptions() {
    populateCropSelect(document.getElementById('field-crop-piantagioni'), 'frutteti', { placeholder: 'Seleziona coltura arborea...' });
    populateCropSelect(document.getElementById('field-crop-campi'), 'campo_aperto', { placeholder: 'Seleziona coltura...' });
    populateCropSelect(document.getElementById('rotate-crop-piantagioni'), 'frutteti');
    populateCropSelect(document.getElementById('rotate-crop-campi'), 'campo_aperto');
}

// Load state from the backend API
let mapInitialized = false;

export function refreshMapView() {
    if (!C.map) return;
    requestAnimationFrame(() => {
        C.map.invalidateSize(true);
        const center = C.map.getCenter();
        const zoom = C.map.getZoom();
        C.map.setView(center, zoom, { animate: false });
        C.map.eachLayer((layer) => {
            if (typeof layer.redraw === 'function') layer.redraw();
        });
    });
}

export function ensureMapReady() {
    if (!mapInitialized) {
        createMap();
        mapInitialized = true;
    }
    refreshMapView();
    return C.map;
}

export function initMap() {
    return ensureMapReady();
}

function createMap() {
    // Center of sample fields in Langhe
    const center = [44.6190, 7.9944];
    
    // Initialize Leaflet Map
    C.map = L.map('map', {
        zoomControl: false
    }).setView(center, 16);

    // Geolocalizza all'avvio per centrare sulla posizione reale dell'utente
    if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lon = pos.coords.longitude;
                C.map.setView([lat, lon], 16);
                C.mapCenteredOnUser = true;
            },
            () => {},
            { timeout: 3000, enableHighAccuracy: false }
        );
    }
    
    // Zoom control position
    L.control.zoom({
        position: 'bottomright'
    }).addTo(C.map);

    // Map Tiles
    C.mapLayers.satellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
    });
    
    C.mapLayers.streets = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    });

    // Default layer is Satellite
    C.mapLayers.satellite.addTo(C.map);

    // Handle Tile Toggle Overlay Buttons
    document.getElementById('btn-sat-tiles').addEventListener('click', () => {
        C.map.removeLayer(C.mapLayers.streets);
        C.mapLayers.satellite.addTo(C.map);
        document.getElementById('btn-sat-tiles').classList.add('active');
        document.getElementById('btn-street-tiles').classList.remove('active');
    });

    document.getElementById('btn-street-tiles').addEventListener('click', () => {
        C.map.removeLayer(C.mapLayers.satellite);
        C.mapLayers.streets.addTo(C.map);
        document.getElementById('btn-street-tiles').classList.add('active');
        document.getElementById('btn-sat-tiles').classList.remove('active');
    });




    function parseCatastoRef(ref) {
    if (!ref || ref === 'N/D') return { comune: 'N/D', foglio: 'N/D', particella: 'N/D' };
    const cleanRef = ref.replace(/^IT\.AGE\.CP\./i, '');
    const dotParts = cleanRef.split('.');
    const particella = dotParts.length > 1 ? dotParts[dotParts.length - 1] : 'N/D';
    const leftPart = dotParts[0];
    let comune = 'N/D';
    let foglio = 'N/D';
    if (leftPart.includes('_')) {
        const parts = leftPart.split('_');
        comune = parts[0];
        const rawFoglio = parts[1];
        if (rawFoglio) {
            const num = parseInt(rawFoglio, 10);
            if (!isNaN(num)) {
                foglio = (num >= 100) ? Math.floor(num / 100).toString() : num.toString();
            } else {
                foglio = rawFoglio;
            }
        }
    } else {
        foglio = leftPart;
    }
    return { comune, foglio, particella, rawRef: ref };
}

    // --- Catasto WMS Layer (Agenzia delle Entrate - INSPIRE) ---
    // Il WMS dell'Agenzia delle Entrate non supporta EPSG:3857, usiamo un trick con EPSG:6706
    C.mapLayers.catasto = L.tileLayer.wms(
        'https://wms.cartografia.agenziaentrate.gov.it/inspire/wms/ows01.php',
        {
            layers: 'CP.CadastralParcel,CP.CadastralZoning',
            format: 'image/png',
            transparent: true,
            version: '1.1.1',
            crs: L.extend({}, L.CRS.EPSG4326, { code: 'EPSG:6706' }),
            attribution: '\u00a9 Agenzia delle Entrate - Catasto',
            opacity: 0.7
        }
    );

    let catastoActive = false;
    const btnCatasto = document.getElementById('btn-catasto-layer');
    const helpBadge = document.querySelector('.map-help-badge');
    if (btnCatasto) {
        btnCatasto.addEventListener('click', () => {
            catastoActive = !catastoActive;
            if (catastoActive) {
                C.mapLayers.catasto.addTo(C.map);
                btnCatasto.classList.add('active');
                btnCatasto.style.borderColor = '#f59e0b';
                btnCatasto.style.color = '#ffffff';
                btnCatasto.style.background = '#d97706';
                // Zoom minimo per visualizzare le particelle
                if (C.map.getZoom() < 15) {
                    C.map.setZoom(15);
                }
                if (helpBadge) helpBadge.innerHTML = '<i class="fa-solid fa-info-circle"></i> Click per tracciare. <b>Click destro / Tieni premuto</b> per info particella.';
            } else {
                C.map.removeLayer(C.mapLayers.catasto);
                btnCatasto.classList.remove('active');
                btnCatasto.style.borderColor = '#d97706';
                btnCatasto.style.color = '#f59e0b';
                btnCatasto.style.background = '';
                if (helpBadge) helpBadge.innerHTML = '<i class="fa-solid fa-info-circle"></i> Fai click sulla mappa per iniziare a tracciare';
            }
        });
    }

    // Popup info particella catastale al click destro (o tap lungo su mobile) quando il layer è attivo
    C.map.on('contextmenu', async (e) => {
        if (!catastoActive) return;
        
        // Disabilitiamo il controllo di C.currentDrawPoints così l'utente può 
        // richiedere le info anche se ha iniziato a disegnare.
        // if (C.currentDrawPoints.length > 0) return; 

        // In demo mode, invita a registrarsi
        if (C.currentUser && C.currentUser.isDemo) {
            L.popup()
                .setLatLng(e.latlng)
                .setContent(`
                    <div style="padding:12px; min-width:220px; text-align:center;">
                        <i class="fa-solid fa-lock" style="font-size:22px; color:var(--warning); margin-bottom:8px; display:block;"></i>
                        <strong style="font-size:13px; display:block; margin-bottom:6px;">Dati catastali riservati</strong>
                        <span style="font-size:12px; color:#8b949e;">Registrati per visualizzare foglio e particella di questa zona.</span>
                    </div>
                `)
                .openOn(C.map);
            return;
        }

        const lat = e.latlng.lat.toFixed(6);
        const lon = e.latlng.lng.toFixed(6);

        // Costruisci GetFeatureInfo request per il layer catasto
        const size = C.map.getSize();
        const bounds = C.map.getBounds();
        // WMS 1.3.0 + EPSG:6706 richiede BBOX in Lat,Lon
        const bbox = `${bounds.getSouthWest().lat},${bounds.getSouthWest().lng},${bounds.getNorthEast().lat},${bounds.getNorthEast().lng}`;
        const point = C.map.latLngToContainerPoint(e.latlng);

        // Usiamo il proxy locale per aggirare il blocco CORS
        const gfiUrl = `/api/catasto?bbox=${bbox}&width=${size.x}&height=${size.y}&i=${Math.round(point.x)}&j=${Math.round(point.y)}`;

        const popup = L.popup().setLatLng(e.latlng);
        popup.setContent('<div style="padding:8px;"><i class="fa-solid fa-spinner fa-spin"></i> Recupero dati catastali...</div>');
        popup.openOn(C.map);

        try {
            const res = await fetch(gfiUrl);
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const gfi = await res.json();
            const features = gfi.features || [];

            if (features.length === 0 && gfi.foglio === 'N/D') {
                popup.setContent('<div style="padding:8px; color:var(--text-muted);"><i class="fa-solid fa-info-circle"></i> Nessuna particella catastale trovata in questo punto.</div>');
            } else {
                const f = features[0] || {};
                const p = f.properties || {};
                const inspireId = gfi.inspireId || p.inspireId || 'N/D';
                const parsed = parseCatastoRef(inspireId);
                const foglio = (gfi.foglio && gfi.foglio !== 'N/D') ? gfi.foglio : parsed.foglio;
                let particella = (gfi.particella && gfi.particella !== 'N/D') ? gfi.particella : parsed.particella;
                if (particella === 'N/D') particella = '';
                
                const particellaDisplay = particella || 'Nessuna / Non Definita';
                
                popup.setContent(`
                    <div style="padding:10px; min-width:200px;">
                        <strong style="font-size:14px;"><i class="fa-solid fa-border-all" style="color:#58a6ff; margin-right:6px;"></i>Dati Catastali Ufficiali</strong>
                        <hr style="margin:8px 0; border-color:rgba(255,255,255,0.1);">
                        <table style="width:100%; font-size:12px; border-collapse:collapse;">
                            <tr><td style="color:#8b949e; padding:3px 6px;">Foglio Catastale</td><td style="font-weight:700; padding:3px 6px; color:#3fb950; font-size:14px;" id="popup-foglio">Foglio ${foglio}</td></tr>
                            <tr><td style="color:#8b949e; padding:3px 6px;">Particella / Mappale</td><td style="font-weight:700; padding:3px 6px; color:#3fb950; font-size:14px;" id="popup-particella">${particellaDisplay}</td></tr>
                            <tr><td style="color:#8b949e; padding:3px 6px;">Codice Riferimento WMS</td><td style="font-size:10px; padding:3px 6px;"><code>${inspireId}</code></td></tr>
                        </table>
                        <button onclick="window.importCatastoData('${foglio}', '${particella}', ${lat}, ${lon});" class="btn btn-secondary btn-sm" style="margin-top: 10px; width: 100%;">
                            <i class="fa-solid fa-download"></i> Importa (Foglio ${foglio}${particella ? ', Particella ' + particella : ''})
                        </button>
                    </div>
                `);
            }
        } catch (err) {
            popup.setContent(`<div style="padding:8px;"><i class="fa-solid fa-triangle-exclamation text-warning"></i> Impossibile recuperare dati catastali: ${err.message}</div>`);
        }
    });

    // Render preloaded fields
    renderSavedFieldsOnMap();

    // Map drawing logic (custom click handlers)
    C.map.on('click', handleMapClickForDrawing);

    document.getElementById('clear-draw-btn').addEventListener('click', clearCurrentDrawing);

    // Toggle macro crop fields
    const macroSelect = document.getElementById('field-macro-category');
    const piantagioniGroup = document.getElementById('field-crop-piantagioni-group');
    const campiGroup = document.getElementById('field-crop-campi-group');
    const piantagioniSelect = document.getElementById('field-crop-piantagioni');
    const campiSelect = document.getElementById('field-crop-campi');
    const customCropGroup = document.getElementById('custom-crop-group');
    const customCropInput = document.getElementById('field-crop-custom');

    if (macroSelect) {
        macroSelect.addEventListener('change', () => {
            const val = macroSelect.value;
            // Hide both
            piantagioniGroup.classList.add('hidden');
            campiGroup.classList.add('hidden');
            customCropGroup.classList.add('hidden');
            piantagioniSelect.required = false;
            campiSelect.required = false;
            customCropInput.required = false;
            piantagioniSelect.value = '';
            campiSelect.value = '';
            customCropInput.value = '';

            if (val === 'Frutteti') {
                piantagioniGroup.classList.remove('hidden');
                piantagioniSelect.required = true;
            } else if (val === 'A campo aperto') {
                campiGroup.classList.remove('hidden');
                campiSelect.required = true;
            }
        });

        // Add change listener to sub-dropdowns to show custom input
        [piantagioniSelect, campiSelect].forEach(select => {
            select.addEventListener('change', () => {
                if (select.value === 'Altro') {
                    customCropGroup.classList.remove('hidden');
                    customCropInput.required = true;
                } else {
                    customCropGroup.classList.add('hidden');
                    customCropInput.required = false;
                    customCropInput.value = '';
                }
            });
        });
    }

    // Logica di Geolocalizzazione
    const geolocateBtn = document.getElementById('btn-geolocate');
    if (geolocateBtn) {
        geolocateBtn.addEventListener('click', () => {
            if (!navigator.geolocation) {
                alert('La geolocalizzazione non è supportata dal tuo browser.');
                return;
            }
            
            geolocateBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Rilevamento...';
            geolocateBtn.disabled = true;
            
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;
                    
                    if (window.userLocationMarker) {
                        window.userLocationMarker.setLatLng([lat, lng]);
                    } else {
                        window.userLocationMarker = L.marker([lat, lng], {
                            icon: L.divIcon({
                                className: 'user-location-marker',
                                html: '<div class="pulse-marker"></div>',
                                iconSize: [20, 20],
                                iconAnchor: [10, 10]
                            })
                        }).addTo(C.map);
                    }
                    C.map.setView([lat, lng], 17);
                    
                    geolocateBtn.innerHTML = '<i class="fa-solid fa-crosshairs"></i> La mia Posizione';
                    geolocateBtn.disabled = false;
                },
                (error) => {
                    console.error('Errore geolocalizzazione:', error);
                    alert('Impossibile determinare la posizione: ' + error.message);
                    geolocateBtn.innerHTML = '<i class="fa-solid fa-crosshairs"></i> La mia Posizione';
                    geolocateBtn.disabled = false;
                },
                { enableHighAccuracy: true, timeout: 10000 }
            );
        });
    }



    // Save field form submission
    document.getElementById('new-field-form').addEventListener('submit', (e) => {
        e.preventDefault();
        saveNewField();
    });

    // Avvio ricalcolo e disegno automatico per particelle accorpate al cambio input
    const triggerGeomUpdate = () => {
        const foglio = document.getElementById('field-foglio').value.trim();
        const particella = document.getElementById('field-particella').value.trim();
        if (foglio && particella) {
            // Usiamo il centro mappa corrente come riferimento per la ricerca catastale
            const center = C.map ? C.map.getCenter() : null;
            const lat = center ? center.lat : null;
            const lon = center ? center.lng : null;
            loadParticelleGeometrie(foglio, particella, lat, lon);
        }
    };

    document.getElementById('field-particella').addEventListener('change', triggerGeomUpdate);
    document.getElementById('field-foglio').addEventListener('change', triggerGeomUpdate);
}

export function renderSavedFieldsOnMap() {
    if (!C.map) return;
    // Clear existing overlay layers
    C.savedFieldLayers.forEach(layer => C.map.removeLayer(layer));
    C.savedFieldLayers = [];

    C.state.fields.forEach(field => {
        let shape;
        const isMultiPolygon = field.coords && Array.isArray(field.coords[0]) && Array.isArray(field.coords[0][0]);
        if (field.coords && field.coords.length === 1 && !isMultiPolygon) {
            shape = L.circleMarker(field.coords[0], {
                radius: 10,
                color: '#3fb950',
                fillColor: '#2ea44f',
                fillOpacity: 0.85,
                weight: 3
            }).addTo(C.map);
        } else if (field.coords && (isMultiPolygon || field.coords.length >= 3)) {
            shape = L.polygon(field.coords, {
                color: '#3fb950',
                fillColor: '#2ea44f',
                fillOpacity: 0.4,
                weight: 3
            }).addTo(C.map);
        } else {
            return;
        }

        shape.fieldData = field;

        // Bind interactive popup con dettagli catastali
        const catastoInfo = (field.foglio || field.particella) 
            ? `<br><span style="font-size:11px; color:#58a6ff;"><i class="fa-solid fa-border-all"></i> Foglio ${field.foglio || '-'}, Part. ${field.particella || '-'}</span>`
            : '';

        shape.bindPopup(`
            <div class="map-popup" style="padding:6px; min-width:180px;">
                <strong style="font-size:14px; color:#3fb950;">🌱 ${field.name}</strong><br>
                <span><b>Coltura:</b> ${field.crop}</span><br>
                <span><b>Superficie:</b> ${field.area} ha</span>
                ${catastoInfo}
                <div style="margin-top:10px;">
                    <button type="button" class="btn btn-secondary btn-sm field-summary-popup-btn" data-field-id="${field.id}" style="width:100%; font-size:12px; padding:6px 8px;">
                        <i class="fa-solid fa-clipboard-list"></i> Riepilogo attività
                    </button>
                </div>
            </div>
        `);
        shape.on('popupopen', () => {
            const btn = document.querySelector(`.field-summary-popup-btn[data-field-id="${field.id}"]`);
            if (btn && !btn.dataset.bound) {
                btn.dataset.bound = '1';
                btn.addEventListener('click', (ev) => {
                    L.DomEvent.stopPropagation(ev);
                    openFieldSummaryModal(field);
                });
            }
        });
        C.savedFieldLayers.push(shape);
    });

    // Inquadra automaticamente tutti i campi dell'azienda sulla mappa (solo se non siamo già centrati sulla posizione reale GPS)
    if (!C.mapCenteredOnUser && C.savedFieldLayers.length > 0 && C.map) {
        try {
            const group = L.featureGroup(C.savedFieldLayers);
            C.map.fitBounds(group.getBounds().pad(0.2));
        } catch(e) {}
    }

    renderFieldsList();
}

export function handleMapClickForDrawing(e) {
    // Drawing is active
    const lat = e.latlng.lat;
    const lng = e.latlng.lng;

    C.currentDrawPoints.push([lat, lng]);

    // Create marker
    const marker = L.circleMarker([lat, lng], {
        radius: 6,
        color: '#2188ff',
        fillColor: '#58a6ff',
        fillOpacity: 0.9
    }).addTo(C.map);
    C.drawMarkers.push(marker);

    // If first point, make it clickable to close the polygon
    if (C.currentDrawPoints.length === 1) {
        marker.on('click', (ev) => {
            L.DomEvent.stopPropagation(ev);
            closeDrawingPolygon();
        });
        marker.bindTooltip("Clicca qui per chiudere il poligono", { permanent: false, direction: 'top' });
    }

    // Redraw polyline/polygon
    if (C.drawPolygonLayer) {
        C.map.removeLayer(C.drawPolygonLayer);
    }

    if (C.currentDrawPoints.length > 2) {
        C.drawPolygonLayer = L.polygon(C.currentDrawPoints, {
            color: '#2188ff',
            fillColor: '#58a6ff',
            fillOpacity: 0.25,
            weight: 2
        }).addTo(C.map);
    } else if (C.currentDrawPoints.length > 1) {
        C.drawPolygonLayer = L.polyline(C.currentDrawPoints, {
            color: '#2188ff',
            weight: 2
        }).addTo(C.map);
    }

    // Calculate area
    if (C.currentDrawPoints.length > 2) {
        const areaHa = calculatePolygonArea(C.currentDrawPoints);
        document.getElementById('field-area').value = areaHa.toFixed(2);
        document.getElementById('save-field-btn').disabled = false;
    }
}

export function closeDrawingPolygon() {
    if (C.currentDrawPoints.length > 2) {
        if (C.drawPolygonLayer) {
            C.map.removeLayer(C.drawPolygonLayer);
        }
        C.drawPolygonLayer = L.polygon(C.currentDrawPoints, {
            color: '#2188ff',
            fillColor: '#58a6ff',
            fillOpacity: 0.4,
            weight: 3
        }).addTo(C.map);
        
        const areaHa = calculatePolygonArea(C.currentDrawPoints);
        document.getElementById('field-area').value = areaHa.toFixed(2);
        alert(`Perimetro chiuso correttamente. Superficie calcolata: ${areaHa.toFixed(2)} ettari.`);
    }
}

export function clearCurrentDrawing() {
    if (C.drawPolygonLayer) {
        C.map.removeLayer(C.drawPolygonLayer);
        C.drawPolygonLayer = null;
    }
    C.drawMarkers.forEach(m => C.map.removeLayer(m));
    C.drawMarkers = [];
    C.currentDrawPoints = [];
    document.getElementById('field-area').value = '';
    document.getElementById('save-field-btn').disabled = true;
    
    // Resetta la cache del catasto caricato
    window._lastLoadedFoglio = null;
    window._lastLoadedParticelle = null;
}

// Spherical earth polygon area calculation
export function canManageFields() {
    return C.currentUser && (C.currentUser.role === 'admin' || C.currentUser.is_delegate === 1);
}

export function canManageInventory() {
    return C.currentUser && (C.currentUser.role === 'admin' || C.currentUser.is_delegate === 1);
}

export function updateFieldFormPermissions() {
    const isAllowed = canManageFields();
    const form = document.getElementById('new-field-form');
    if (!form) return;

    const fieldsToToggle = [
        'field-name', 'field-macro-category', 'field-crop-piantagioni', 
        'field-crop-campi', 'field-crop-custom', 'field-foglio', 'field-particella', 
        'save-field-btn', 'clear-draw-btn', 'btn-catasto-layer'
    ];

    fieldsToToggle.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.disabled = !isAllowed;
    });

    let notice = document.getElementById('field-permission-notice');
    if (!isAllowed) {
        if (!notice) {
            notice = document.createElement('div');
            notice.id = 'field-permission-notice';
            notice.style.cssText = 'background:rgba(217,119,6,0.15); border:1px solid #d97706; color:#f59e0b; padding:10px; border-radius:6px; font-size:12px; margin-bottom:12px;';
            notice.innerHTML = '<i class="fa-solid fa-lock"></i> <strong>Modello Disattivato (Sola Lettura)</strong><br>Solo l\'Amministratore o un Operatore con Delega Amministrativa può inserire e modificare campi ed appezzamenti.';
            form.prepend(notice);
        }
    } else {
        if (notice) notice.remove();
    }
}

export function saveNewField() {
    if (C.currentUser && C.currentUser.isDemo) {
        showRegisterRequiredAlert('salvare nuovi campi sulla mappa');
        return;
    }
    if (!canManageFields()) {
        alert('⛔ Permesso negato: Soltanto l\'Amministratore o un Operatore con Delega Amministrativa può aggiungere o modificare appezzamenti e campi agricoli.');
        return;
    }
    const name = document.getElementById('field-name').value.trim();
    const macroCategory = document.getElementById('field-macro-category').value;
    const area = parseFloat(document.getElementById('field-area').value);
    
    let cropName = '';
    if (macroCategory === 'Frutteti') {
        cropName = document.getElementById('field-crop-piantagioni').value;
    } else if (macroCategory === 'A campo aperto') {
        cropName = document.getElementById('field-crop-campi').value;
    }

    if (cropName === 'Altro') {
        cropName = document.getElementById('field-crop-custom').value.trim() || 'Altro';
    }

    if (!name || !macroCategory || !cropName || isNaN(area)) {
        alert('Compilare tutti i campi obbligatori (Nome Campo, Coltura e Superficie).');
        return;
    }

    const foglio = document.getElementById('field-foglio').value.trim();
    const particella = document.getElementById('field-particella').value.trim();

    if (particella) {
        const contiguita = checkParticelleContiguita(particella);
        if (!contiguita.isValid) {
            const proceed = confirm(`⚠️ Rilevate particelle non contigue/non consecutive:\n${contiguita.reason}\n\nConfermi che le particelle sono comunque fisicamente confinanti (es. per accorpamenti ex novo, denominazioni storiche o bis/ter) e vuoi procedere con il salvataggio?`);
            if (!proceed) {
                return;
            }
        }
    }

    if (C.currentDrawPoints.length === 0 && !foglio) {
        alert('Inserisci almeno il Foglio catastale o indica la posizione sulla mappa.');
        return;
    }

    const cropFormatted = `${macroCategory}: ${cropName}`;

    // Se non ci sono punti tracciati ma ci sono Foglio e Particella, usa la posizione corrente della mappa come punto
    let finalCoords = C.currentDrawPoints;
    if (finalCoords.length === 0 && C.map) {
        const center = C.map.getCenter();
        finalCoords = [[center.lat, center.lng]];
    }

    const newField = {
        name: name,
        crop: cropFormatted,
        area: area,
        coords: finalCoords,
        foglio: foglio,
        particella: particella
    };

    api.saveField(newField)
        .then(() => {
            // Reset Form & Drawing
            document.getElementById('new-field-form').reset();
            document.getElementById('field-crop-piantagioni-group').classList.add('hidden');
            document.getElementById('field-crop-campi-group').classList.add('hidden');
            document.getElementById('custom-crop-group').classList.add('hidden');
            
            document.getElementById('field-crop-piantagioni').required = false;
            document.getElementById('field-crop-campi').required = false;
            document.getElementById('field-crop-custom').required = false;
            
            document.getElementById('field-foglio').value = '';
            document.getElementById('field-particella').value = '';
            clearCurrentDrawing();
            
            // Redraw Saved fields
            loadApplicationData();
            alert('Campo salvato con successo!');
        })
        .catch(err => {
            alert('Errore nel salvataggio del campo: ' + err.message);
        });
}

export function renderFieldsList() {
    const container = document.getElementById('fields-list-container');
    const tableBody = document.getElementById('map-fields-table-body');
    const totalBadge = document.getElementById('fields-total-badge');

    if (container) container.innerHTML = '';
    if (tableBody) tableBody.innerHTML = '';

    const totalArea = C.state.fields.reduce((acc, f) => acc + (f.area || 0), 0);
    if (totalBadge) {
        totalBadge.textContent = `${C.state.fields.length} Campi • ${totalArea.toFixed(2)} ha totali`;
    }

    if (C.state.fields.length === 0) {
        if (container) container.innerHTML = `<div class="empty-list">Nessun campo registrato. Usa la mappa a destra per inserire o tracciare.</div>`;
        if (tableBody) tableBody.innerHTML = `<tr><td colspan="6" style="text-align:center; color:var(--text-muted); padding:20px;">Nessun campo registrato. Usa la mappa sopra o il modulo a sinistra per aggiungere il primo appezzamento.</td></tr>`;
        return;
    }

    C.state.fields.forEach(field => {
        // Render Sidebar List Item
        if (container) {
            const item = document.createElement('div');
            item.className = 'field-item';
            item.innerHTML = `
                <div class="field-item-details">
                    <span class="field-item-name">${field.name}</span>
                    <span class="field-item-meta">${field.crop} &bull; ${field.area.toFixed(2)} ha</span>
                    ${(field.foglio || field.particella) ? `<span class="field-item-meta" style="font-size:11px; opacity: 0.8;"><i class="fa-solid fa-map-location-dot"></i> Fg. ${field.foglio || '-'} / Part. ${field.particella || '-'}</span>` : ''}
                </div>
                <div class="field-item-actions" style="display: flex; gap: 8px;">
                    <button class="btn-icon rotate-btn" data-id="${field.id}" title="Correggi coltura annata" style="color: var(--primary);">
                        <i class="fa-solid fa-arrows-rotate"></i>
                    </button>
                    <button class="btn-icon delete-btn" data-id="${field.id}" title="Elimina Campo (Affitto Scaduto)">
                        <i class="fa-solid fa-trash-can"></i>
                    </button>
                </div>
            `;
            
            item.addEventListener('click', (e) => {
                if (e.target.closest('.btn-icon')) return;
                openFieldSummaryModal(field);
            });

            item.querySelector('.rotate-btn')?.addEventListener('click', (e) => {
                e.stopPropagation();
                openCropRotateModal(field);
            });

            item.querySelector('.delete-btn')?.addEventListener('click', (e) => {
                e.stopPropagation();
                confirmAndDeleteField(field);
            });

            container.appendChild(item);
        }

        // Render Table Row Below Map
        if (tableBody) {
            const tr = document.createElement('tr');
            const catastoStr = (field.foglio || field.particella)
                ? `Foglio ${field.foglio || '-'}, Part. ${field.particella || '-'}`
                : 'N.D.';

            tr.className = 'map-fields-row-clickable';
            tr.innerHTML = `
                <td><strong>${field.name}</strong><br><small style="color:var(--text-muted);">Clicca per riepilogo attività</small></td>
                <td><span class="badge badge-info" style="background:rgba(88,166,255,0.1); color:#58a6ff; border:1px solid rgba(88,166,255,0.3);">${field.crop}</span></td>
                <td><code>${catastoStr}</code></td>
                <td><strong>${field.area.toFixed(2)} ha</strong></td>
                <td><span class="badge badge-success" style="background:#15803d; color:#fff;">🟢 Attivo</span></td>
                <td>
                    <div style="display:flex; gap:8px; justify-content:center; flex-wrap:wrap;">
                        <button class="btn btn-secondary btn-sm btn-field-summary" data-id="${field.id}" title="Riepilogo attività" style="padding:4px 10px; font-size:12px; color:var(--primary);">
                            <i class="fa-solid fa-clipboard-list"></i> Riepilogo
                        </button>
                        <button class="btn btn-secondary btn-sm btn-center-map" data-id="${field.id}" title="Centra sulla Mappa" style="padding:4px 10px; font-size:12px;">
                            <i class="fa-solid fa-crosshairs"></i> Centra
                        </button>
                        <button class="btn btn-secondary btn-sm btn-rotate-crop" data-id="${field.id}" title="Correggi coltura annata" style="padding:4px 10px; font-size:12px; color:var(--primary);">
                            <i class="fa-solid fa-arrows-rotate"></i> Coltura
                        </button>
                        <button class="btn btn-danger btn-sm btn-delete-map-field" data-id="${field.id}" title="Elimina Campo (Affitto Scaduto / Non Rinnovato)" style="padding:4px 10px; font-size:12px;">
                            <i class="fa-solid fa-trash-can"></i> Rimuovi
                        </button>
                    </div>
                </td>
            `;

            tr.addEventListener('click', (e) => {
                if (e.target.closest('button')) return;
                openFieldSummaryModal(field);
            });
            tr.querySelector('.btn-field-summary')?.addEventListener('click', (e) => {
                e.stopPropagation();
                openFieldSummaryModal(field);
            });
            tr.querySelector('.btn-center-map')?.addEventListener('click', (e) => {
                e.stopPropagation();
                focusMapOnField(field);
            });
            tr.querySelector('.btn-rotate-crop')?.addEventListener('click', (e) => {
                e.stopPropagation();
                openCropRotateModal(field);
            });
            tr.querySelector('.btn-delete-map-field')?.addEventListener('click', (e) => {
                e.stopPropagation();
                confirmAndDeleteField(field);
            });

            tableBody.appendChild(tr);
        }
    });
}

export function focusMapOnField(field) {
    if (!field) return;
    
    // Switch al tab della mappa se siamo su una dashboard o altro registro
    const mapTabBtn = document.querySelector('[data-tab="fields-map"]');
    if (mapTabBtn) mapTabBtn.click();

    setTimeout(() => {
        if (!C.map) return;
        C.map.invalidateSize();

        let centerLatLng = null;
        if (field.coords && field.coords.length === 1) {
            centerLatLng = field.coords[0];
            C.map.setView(centerLatLng, 17, { animate: true });
        } else if (field.coords && field.coords.length >= 3) {
            const poly = L.polygon(field.coords);
            C.map.fitBounds(poly.getBounds().pad(0.2), { animate: true });
            centerLatLng = poly.getBounds().getCenter();
        }

        // Trova il layer salvato associato al campo ed apri il popup
        const layer = C.savedFieldLayers.find(l => l.fieldData && l.fieldData.id === field.id);
        if (layer) {
            layer.openPopup();
        } else if (centerLatLng) {
            L.popup()
                .setLatLng(centerLatLng)
                .setContent(`
                    <div style="padding:6px; min-width:180px;">
                        <strong style="font-size:14px; color:#3fb950;">🌱 ${field.name}</strong><br>
                        <span><b>Coltura:</b> ${field.crop}</span><br>
                        <span><b>Superficie:</b> ${field.area} ha</span>
                        ${(field.foglio || field.particella) ? `<br><span style="font-size:11px; color:#58a6ff;">Foglio ${field.foglio || '-'}, Part. ${field.particella || '-'}</span>` : ''}
                    </div>
                `)
                .openOn(C.map);
        }
    }, 150);
}


export function getFieldActivities(field, { allYears = false } = {}) {
    if (!field) return [];
    const year = String(C.state.currentCampaignYear || new Date().getFullYear()).substring(0, 4);
    return (C.state.treatments || [])
        .filter(a => activityMatchesField(a, field))
        .filter(a => {
            if (allYears) return true;
            const cy = String(a.campaignYear || (a.date || '')).substring(0, 4);
            return cy === year || cy.startsWith(year);
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date));
}
export function renderFieldSummaryContent() {
    const field = C.fieldSummaryCurrentField;
    if (!field) return;

    const allYears = document.getElementById('field-summary-all-years')?.checked || false;
    const activities = getFieldActivities(field, { allYears });
    const tbody = document.getElementById('field-summary-table-body');
    const countEl = document.getElementById('field-summary-count');
    const metaEl = document.getElementById('field-summary-meta');
    const titleEl = document.getElementById('field-summary-title');
    const subtitleEl = document.getElementById('field-summary-subtitle');

    if (titleEl) titleEl.textContent = field.name;
    if (subtitleEl) {
        const yearLabel = C.state.currentCampaignYear || new Date().getFullYear();
        subtitleEl.textContent = allYears
            ? 'Storico completo delle operazioni registrate su questo appezzamento'
            : `Operazioni dell'annata agraria ${yearLabel}`;
    }

    if (metaEl) {
        const catasto = (field.foglio || field.particella)
            ? `Fg. ${field.foglio || '-'} / Part. ${field.particella || '-'}`
            : 'Catasto N.D.';
        metaEl.innerHTML = `
            <span class="field-summary-chip"><i class="fa-solid fa-seedling"></i> ${field.crop}</span>
            <span class="field-summary-chip"><i class="fa-solid fa-ruler-combined"></i> ${field.area.toFixed(2)} ha</span>
            <span class="field-summary-chip"><i class="fa-solid fa-map-location-dot"></i> ${catasto}</span>
            <span class="field-summary-chip"><i class="fa-solid fa-clipboard-list"></i> ${activities.length} attività</span>
        `;
    }

    if (countEl) {
        countEl.textContent = activities.length === 1 ? '1 operazione trovata' : `${activities.length} operazioni trovate`;
    }

    if (!tbody) return;
    tbody.innerHTML = '';

    if (activities.length === 0) {
        tbody.innerHTML = `<tr class="empty-row"><td colspan="4">Nessuna attività registrata su questo campo${allYears ? '' : ' nell\'annata corrente'}. Registra aratura, concimazione, trattamenti e altre operazioni dal menu «Registra Attività».</td></tr>`;
        return;
    }

    activities.forEach(a => {
        const tr = document.createElement('tr');
        const desc = a.description || a.product || '—';
        const details = a.details || (a.product ? `Prodotto: ${a.product}` : '—');
        tr.innerHTML = `
            <td>${formatDate(a.date)}</td>
            <td><span class="badge badge-success">${formatActivityTypeLabel(a.activityType)}</span></td>
            <td><strong>${desc}</strong></td>
            <td><small style="white-space:pre-line;">${details}</small></td>
        `;
        tbody.appendChild(tr);
    });
}

export function closeFieldSummaryModal() {
    const modal = document.getElementById('field-summary-modal');
    if (!modal) return;
    modal.classList.remove('show');
    setTimeout(() => {
        modal.style.display = 'none';
        modal.classList.add('hidden');
    }, 300);
}

export function openFieldSummaryModal(field) {
    if (!field) return;
    C.fieldSummaryCurrentField = field;
    const modal = document.getElementById('field-summary-modal');
    if (!modal) return;

    const allYearsChk = document.getElementById('field-summary-all-years');
    if (allYearsChk) allYearsChk.checked = false;

    renderFieldSummaryContent();

    modal.classList.remove('hidden');
    modal.style.display = 'flex';
    setTimeout(() => modal.classList.add('show'), 10);
}

export function initFieldSummaryHandlers() {
    const modal = document.getElementById('field-summary-modal');
    if (!modal) return;

    const closeBtn = document.getElementById('close-field-summary-modal');
    closeBtn?.addEventListener('click', closeFieldSummaryModal);
    modal.addEventListener('click', (e) => {
        if (e.target === modal) closeFieldSummaryModal();
    });

    document.getElementById('field-summary-all-years')?.addEventListener('change', renderFieldSummaryContent);

    document.getElementById('field-summary-center-map')?.addEventListener('click', () => {
        if (!C.fieldSummaryCurrentField) return;
        closeFieldSummaryModal();
        focusMapOnField(C.fieldSummaryCurrentField);
    });

    document.getElementById('field-summary-fix-crop')?.addEventListener('click', () => {
        if (!C.fieldSummaryCurrentField) return;
        const field = C.fieldSummaryCurrentField;
        closeFieldSummaryModal();
        openCropRotateModal(field);
    });

    document.getElementById('field-summary-open-log')?.addEventListener('click', () => {
        if (!C.fieldSummaryCurrentField) return;
        const fieldName = C.fieldSummaryCurrentField.name;
        closeFieldSummaryModal();
        switchTab('treatment-log');
        const search = document.getElementById('log-search');
        if (search) {
            search.value = fieldName;
            renderFullLogTable();
        }
    });
}

export function openCropRotateModal(field) {
    const modal = document.getElementById('crop-rotate-modal');
    if (!modal) return;
    document.getElementById('rotate-field-id').value = field.id;
    document.getElementById('rotate-field-name-display').textContent = field.name;
    
    const parts = field.crop.split(': ');
    if (parts.length === 2) {
        document.getElementById('rotate-macro-category').value = parts[0];
        document.getElementById('rotate-macro-category').dispatchEvent(new Event('change'));
        
        const cropVal = parts[1];
        if (parts[0] === 'Frutteti') {
            const piantagioniSelect = document.getElementById('rotate-crop-piantagioni');
            const hasOption = Array.from(piantagioniSelect.options).some(o => o.value === cropVal);
            if (hasOption) {
                piantagioniSelect.value = cropVal;
                piantagioniSelect.dispatchEvent(new Event('change'));
            } else {
                piantagioniSelect.value = 'Altro';
                piantagioniSelect.dispatchEvent(new Event('change'));
                document.getElementById('rotate-crop-custom').value = cropVal;
            }
        } else {
            const campiSelect = document.getElementById('rotate-crop-campi');
            const hasOption = Array.from(campiSelect.options).some(o => o.value === cropVal);
            if (hasOption) {
                campiSelect.value = cropVal;
                campiSelect.dispatchEvent(new Event('change'));
            } else {
                campiSelect.value = 'Altro';
                campiSelect.dispatchEvent(new Event('change'));
                document.getElementById('rotate-crop-custom').value = cropVal;
            }
        }
    }
    
    modal.style.display = 'flex';
    modal.classList.remove('hidden');
    setTimeout(() => modal.classList.add('show'), 10);
}

export function confirmAndDeleteField(field) {
    if (C.currentUser && C.currentUser.isDemo) {
        showRegisterRequiredAlert('eliminare campi');
        return;
    }
    if (C.currentUser && C.currentUser.role !== 'admin') {
        alert('⛔ Permesso negato: Soltanto l\'Amministratore dell\'azienda può eliminare o dismettere appezzamenti.');
        return;
    }
    if (confirm(`Sei sicuro di voler eliminare l'appezzamento "${field.name}"?\n\nQuesta funzione è pensata per contratti di affitto non rinnovati o terreni non più coltivati. Il campo verrà rimosso dalla mappa.`)) {
        api.deleteField(field.id)
            .then(() => {
                alert(`Campo "${field.name}" rimosso con successo.`);
                loadApplicationData();
            })
            .catch(err => alert('Errore nell\'eliminazione del campo: ' + err.message));
    }
}

export async function loadParticelleGeometrie(foglio, particellaStr, lat, lon) {
    if (!foglio || !particellaStr) return;

    // Se le particelle e il foglio sono esattamente quelli già caricati, evitiamo ricalcoli inutili
    if (window._lastLoadedFoglio === foglio && window._lastLoadedParticelle === particellaStr) {
        return;
    }

    // Se lat/lon non sono forniti, prova a usare il centro della mappa come riferimento
    if (!lat || !lon) {
        if (C.map) {
            const center = C.map.getCenter();
            lat = center.lat;
            lon = center.lng;
        } else {
            lat = 41.9; 
            lon = 13.0;
        }
    }

    const contiguita = checkParticelleContiguita(particellaStr);
    const parts = contiguita.expanded || particellaStr.split(/[,;]/).map(p => p.trim()).filter(Boolean);
    if (parts.length === 0) return;

    const btn = document.getElementById('save-field-btn');
    const oldText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Ricerca geometrie...';

    try {
        // Carichiamo tutte le particelle in parallelo dal WFS
        const token = api.getToken();
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        const promises = parts.map(async (part) => {
            const res = await fetch(`/api/catasto-cerca?comune=&foglio=${foglio}&particella=${part}&lat=${lat}&lng=${lon}`, { headers: authHeaders });
            if (!res.ok) throw new Error(`Particella ${part} non trovata`);
            return await res.json();
        });

        const results = await Promise.all(promises);
        
        // Pulisce disegni attivi
        clearCurrentDrawing();

        const allRings = [];
        let totalArea = 0;

        results.forEach(data => {
            if (data && data.found && data.rings && data.rings.length > 0) {
                // Primo anello (rings[0]) = coordinate esterne
                const extCoords = data.rings[0].map(c => [c[0], c[1]]);
                allRings.push(extCoords);
                
                // Calcola l'area della singola particella e la somma
                totalArea += calculatePolygonArea(extCoords);
            }
        });

        if (allRings.length > 0) {
            // Se abbiamo una sola particella, l'array è semplice, altrimenti è un MultiPolygon
            C.currentDrawPoints = allRings.length === 1 ? allRings[0] : allRings;

            // Disegniamo il perimetro o i perimetri uniti
            C.drawPolygonLayer = L.polygon(C.currentDrawPoints, {
                color: '#2188ff',
                fillColor: '#58a6ff',
                fillOpacity: 0.5,
                weight: 3
            }).addTo(C.map);

            // Popoliamo il campo area
            document.getElementById('field-area').value = totalArea.toFixed(4);

            // Centra la mappa per inquadrare tutti i poligoni accorpati
            C.map.fitBounds(C.drawPolygonLayer.getBounds().pad(0.15));

            // Memorizza nella cache catastale gli identificativi appena caricati con successo
            window._lastLoadedFoglio = foglio;
            window._lastLoadedParticelle = particellaStr;

            btn.disabled = false;
            btn.innerHTML = oldText;
            
            // Abilita il bottone di salvataggio
            document.getElementById('save-field-btn').disabled = false;
        } else {
            throw new Error('Nessuna geometria valida caricata.');
        }
    } catch (err) {
        console.warn('Errore caricamento geometrie accorpate:', err.message);
        btn.disabled = false;
        btn.innerHTML = oldText;
        
        // Fallback sul posizionamento del punto singolo se il WFS fallisce (es. server AdE occupato)
        if (lat && lon) {
            clearCurrentDrawing();
            C.currentDrawPoints = [[lat, lon]];
            
            const marker = L.circleMarker([lat, lon], {
                radius: 8,
                color: '#2188ff',
                fillColor: '#58a6ff',
                fillOpacity: 0.9,
                weight: 2
            }).addTo(C.map);
            C.drawMarkers.push(marker);
            
            document.getElementById('field-area').value = '';
            document.getElementById('save-field-btn').disabled = false;
            
            alert(`⚠️ Impossibile ricavare il perimetro automatico di alcune particelle.\nÈ stato posizionato un marker al centro del terreno. Disegna il perimetro manualmente o inserisci l'area stimata.`);
        }
    }
}

window.importCatastoData = async function(foglio, particella, lat, lon) {
    document.getElementById('field-foglio').value = foglio;
    document.getElementById('field-particella').value = particella;
    
    // Richiama il caricamento geometrico unificato
    await loadParticelleGeometrie(foglio, particella, lat, lon);
};
